import { stripEntityIdPrefix } from './entityId';

// 엔티티 참조를 클릭한 화면. 홈이면 홈 액티비티 패널로, 프로젝트면 상세 페이지로 전환된다.
export type EntitySurface = 'home' | 'project';

// tiptap·마크다운에서 쓰는 camelCase 엔티티 타입 → go 라우터 type 세그먼트
const GO_TYPE_BY_ENTITY_TYPE: Record<string, string> = {
  plan: 'plan',
  planTask: 'plan-task',
  convention: 'convention',
  completionReport: 'completion-report',
  postMortem: 'post-mortem',
  coAction: 'co-action',
  codeReview: 'code-review',
  document: 'document',
  skill: 'skill',
};

// 마크다운 href에 직렬화되는 내부 엔티티 참조 프로토콜 (예: "plan:<id>", "convention:<id>:.path")
const INTERNAL_ENTITY_REF_PATTERN = new RegExp(`^(${Object.keys(GO_TYPE_BY_ENTITY_TYPE).join('|')}):(.+)$`);

// 현재 URL로 surface를 판별한다. 홈 라우트가 아니면 프로젝트로 본다(SSR 포함 기본값 project).
export const resolveEntitySurface = (): EntitySurface => {
  if (typeof window === 'undefined') {
    return 'project';
  }
  return window.location.pathname.startsWith('/home') ? 'home' : 'project';
};

// 홈은 액티비티 패널(view 미설정), 프로젝트는 상세 페이지(view=full)로 전환된다.
export const surfaceToGoView = (surface: EntitySurface): 'full' | undefined =>
  surface === 'project' ? 'full' : undefined;

// 엔티티 타입/ID를 surface 기준 go href로 만든다. 내부 엔티티가 아니거나 ID가 비면 null.
export const buildEntityGoHref = (
  entityType: string,
  entityId: string,
  surface: EntitySurface = resolveEntitySurface(),
): string | null => {
  const goType = GO_TYPE_BY_ENTITY_TYPE[entityType];
  if (!goType || entityId.length === 0) {
    return null;
  }

  const params = new URLSearchParams();
  params.set('type', goType);
  params.set('id', stripEntityIdPrefix(entityId));
  const view = surfaceToGoView(surface);
  if (view) {
    params.set('view', view);
  }
  return `/go?${params.toString()}`;
};

// 마크다운 링크 href가 내부 엔티티 참조 프로토콜인지 여부. urlTransform 보존 판단에 사용.
export const isInternalEntityRefHref = (href: string): boolean => INTERNAL_ENTITY_REF_PATTERN.test(href);

// 마크다운 엔티티 참조 href("plan:<id>" 등)를 surface 기준 go href로 변환한다.
// 내부 엔티티가 아니면(외부 이슈·일반 링크 포함) null을 반환한다.
export const buildEntityGoHrefFromRefHref = (
  href: string,
  surface: EntitySurface = resolveEntitySurface(),
): string | null => {
  const match = href.match(INTERNAL_ENTITY_REF_PATTERN);
  if (!match) {
    return null;
  }

  const entityType = match[1];
  let entityId = match[2];
  // convention·skill은 "id:.agentteams/path" 형태일 수 있어 path 부분을 떼어낸다.
  if (entityType === 'convention' || entityType === 'skill') {
    const colonIdx = entityId.indexOf(':');
    if (colonIdx !== -1) {
      entityId = entityId.slice(0, colonIdx);
    }
  }

  return buildEntityGoHref(entityType, entityId, surface);
};
